import { motion, type Variants } from 'framer-motion';

export type MascotMood = 'idle' | 'correct' | 'celebration' | 'streak' | 'hearts_out';

interface MascotProps {
  mood?: MascotMood;
  sport?: string;
  size?: number;
  className?: string;
}

// Sport-specific gear colors & accessories
const SPORT_GEAR: Record<string, { primary: string; secondary: string; accent: string; accessory: 'helmet' | 'headband' | 'cap' | 'visor' }> = {
  football: { primary: '#1B365D', secondary: '#C9A84C', accent: '#FFFFFF', accessory: 'helmet' },
  soccer:   { primary: '#2D9B4F', secondary: '#FFFFFF', accent: '#1A1A2E', accessory: 'headband' },
  basketball: { primary: '#E8651A', secondary: '#FFFFFF', accent: '#1A1A2E', accessory: 'headband' },
  baseball: { primary: '#1B365D', secondary: '#C41E3A', accent: '#FFFFFF', accessory: 'cap' },
};

const moodAnimations: Record<MascotMood, Variants> = {
  idle: {
    animate: {
      y: [0, -3, 0],
      transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
    },
  },
  correct: {
    animate: {
      scale: [1, 1.15, 1],
      rotate: [0, -5, 5, 0],
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  },
  celebration: {
    animate: {
      y: [0, -20, 0, -12, 0],
      rotate: [0, -8, 8, -4, 0],
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  },
  streak: {
    animate: {
      scale: [1, 1.2, 1.05, 1.15, 1],
      rotate: [0, -10, 10, -5, 0],
      transition: { duration: 1, ease: 'easeOut' },
    },
  },
  hearts_out: {
    animate: {
      y: [0, 2, 0],
      rotate: [0, -2, 2, 0],
      transition: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
    },
  },
};

// Eye expression per mood
function getEyes(mood: MascotMood) {
  switch (mood) {
    case 'correct':
      // Happy squint
      return (
        <>
          <path d="M30 42 Q35 38 40 42" stroke="#1A1A2E" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M52 42 Q57 38 62 42" stroke="#1A1A2E" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
    case 'celebration':
      // Star eyes
      return (
        <>
          <circle cx="35" cy="41" r="5" fill="#1A1A2E" />
          <circle cx="35" cy="41" r="2" fill="#FFFFFF" />
          <circle cx="57" cy="41" r="5" fill="#1A1A2E" />
          <circle cx="57" cy="41" r="2" fill="#FFFFFF" />
          <circle cx="33" cy="39" r="1.5" fill="#FFFFFF" opacity="0.7" />
          <circle cx="55" cy="39" r="1.5" fill="#FFFFFF" opacity="0.7" />
        </>
      );
    case 'streak':
      // Fired up eyes
      return (
        <>
          <ellipse cx="35" cy="41" rx="4.5" ry="5.5" fill="#1A1A2E" />
          <ellipse cx="57" cy="41" rx="4.5" ry="5.5" fill="#1A1A2E" />
          <circle cx="33" cy="39" r="2" fill="#FF6B35" opacity="0.8" />
          <circle cx="55" cy="39" r="2" fill="#FF6B35" opacity="0.8" />
        </>
      );
    case 'hearts_out':
      // Sad droopy eyes
      return (
        <>
          <ellipse cx="35" cy="43" rx="4" ry="3.5" fill="#1A1A2E" />
          <ellipse cx="57" cy="43" rx="4" ry="3.5" fill="#1A1A2E" />
          <path d="M28 37 L42 39" stroke="#1A1A2E" strokeWidth="2" strokeLinecap="round" />
          <path d="M64 37 L50 39" stroke="#1A1A2E" strokeWidth="2" strokeLinecap="round" />
          {/* Tear */}
          <motion.ellipse
            cx="39" cy="48" rx="1.5" ry="2"
            fill="#60A5FA"
            animate={{ y: [0, 6, 0], opacity: [0.8, 0, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </>
      );
    default:
      // Neutral idle
      return (
        <>
          <circle cx="35" cy="41" r="4.5" fill="#1A1A2E" />
          <circle cx="57" cy="41" r="4.5" fill="#1A1A2E" />
          <circle cx="33.5" cy="39.5" r="1.8" fill="#FFFFFF" />
          <circle cx="55.5" cy="39.5" r="1.8" fill="#FFFFFF" />
        </>
      );
  }
}

// Mouth expression per mood
function getMouth(mood: MascotMood) {
  switch (mood) {
    case 'correct':
    case 'celebration':
    case 'streak':
      return <path d="M38 52 Q46 60 54 52" stroke="#1A1A2E" strokeWidth="2.5" fill="none" strokeLinecap="round" />;
    case 'hearts_out':
      return <path d="M38 56 Q46 50 54 56" stroke="#1A1A2E" strokeWidth="2" fill="none" strokeLinecap="round" />;
    default:
      return <path d="M40 53 Q46 57 52 53" stroke="#1A1A2E" strokeWidth="2" fill="none" strokeLinecap="round" />;
  }
}

// Sport-specific gear rendered on head
function getGear(sport: string) {
  const gear = SPORT_GEAR[sport] || SPORT_GEAR.football;
  
  switch (gear.accessory) {
    case 'helmet':
      return (
        <g>
          {/* Football helmet */}
          <path d="M22 32 Q22 14 46 12 Q70 14 70 32 L70 36 Q70 28 46 26 Q22 28 22 36 Z" fill={gear.primary} />
          <path d="M70 28 L74 32 L74 40 L70 36" fill={gear.secondary} />
          {/* Facemask */}
          <path d="M26 36 Q26 42 30 44" stroke={gear.secondary} strokeWidth="2" fill="none" />
          <path d="M26 38 Q28 44 32 44" stroke={gear.secondary} strokeWidth="1.5" fill="none" />
          {/* Center stripe */}
          <path d="M46 12 L46 26" stroke={gear.secondary} strokeWidth="2.5" />
        </g>
      );
    case 'headband':
      return (
        <g>
          {/* Headband */}
          <path d="M24 32 Q24 28 46 26 Q68 28 68 32" stroke={gear.primary} strokeWidth="5" fill="none" strokeLinecap="round" />
          {/* Sport logo dot */}
          <circle cx="46" cy="29" r="3" fill={gear.secondary} stroke={gear.primary} strokeWidth="1" />
        </g>
      );
    case 'cap':
      return (
        <g>
          {/* Baseball cap */}
          <path d="M22 34 Q22 16 46 14 Q70 16 70 34 L22 34 Z" fill={gear.primary} />
          {/* Brim */}
          <path d="M18 34 Q16 36 20 38 L48 40 Q56 38 52 34" fill={gear.primary} />
          <path d="M18 34 Q16 36 20 38 L48 40 Q56 38 52 34" fill={gear.secondary} opacity="0.3" />
          {/* Button on top */}
          <circle cx="46" cy="14" r="2" fill={gear.secondary} />
        </g>
      );
    default:
      return null;
  }
}

// Mood-specific effects (particles, sparkles, etc.)
function getMoodEffects(mood: MascotMood) {
  switch (mood) {
    case 'correct':
      return (
        <>
          <motion.text x="70" y="20" fontSize="14" animate={{ y: [20, 8], opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1] }} transition={{ duration: 0.8 }}>✓</motion.text>
          {[0, 1, 2].map(i => (
            <motion.circle
              key={i}
              cx={65 + i * 8}
              cy={25 - i * 5}
              r="2"
              fill="hsl(var(--success))"
              animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0] }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
            />
          ))}
        </>
      );
    case 'celebration':
      return (
        <>
          {[0, 1, 2, 3, 4].map(i => {
            const angle = (i / 5) * Math.PI * 2;
            const cx = 46 + Math.cos(angle) * 42;
            const cy = 40 + Math.sin(angle) * 42;
            return (
              <motion.text
                key={i}
                x={cx}
                y={cy}
                fontSize="10"
                animate={{ opacity: [0, 1, 0], scale: [0.5, 1.3, 0], y: [cy, cy - 15] }}
                transition={{ duration: 1, delay: i * 0.1 }}
              >
                {['⭐', '🎉', '✨', '💫', '🌟'][i]}
              </motion.text>
            );
          })}
        </>
      );
    case 'streak':
      return (
        <>
          <motion.text x="14" y="18" fontSize="16" animate={{ scale: [0, 1.3, 1], rotate: [0, 15, 0] }} transition={{ duration: 0.6, delay: 0.1 }}>🔥</motion.text>
          <motion.text x="64" y="14" fontSize="14" animate={{ scale: [0, 1.3, 1], rotate: [0, -15, 0] }} transition={{ duration: 0.6, delay: 0.3 }}>🔥</motion.text>
          <motion.text x="38" y="8" fontSize="12" animate={{ scale: [0, 1.2, 1], y: [8, 2, 5] }} transition={{ duration: 0.8, delay: 0.2 }}>⚡</motion.text>
        </>
      );
    case 'hearts_out':
      return (
        <>
          <motion.text x="60" y="15" fontSize="14" animate={{ y: [15, 8, 15], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>💔</motion.text>
        </>
      );
    default:
      return null;
  }
}

export function Mascot({ mood = 'idle', sport = 'football', size = 92, className }: MascotProps) {
  const variants = moodAnimations[mood];

  return (
    <motion.div
      className={className}
      variants={variants}
      animate="animate"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 92 92" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Body */}
        <ellipse cx="46" cy="72" rx="20" ry="14" fill="#FFD180" />
        
        {/* Head - round owl shape */}
        <ellipse cx="46" cy="42" rx="26" ry="24" fill="#FFE0A0" />
        
        {/* Facial disk (owl-like lighter area) */}
        <ellipse cx="46" cy="44" rx="20" ry="17" fill="#FFF3D6" />

        {/* Beak */}
        <path d="M43 48 L46 53 L49 48" fill="#FF9800" />

        {/* Eyes */}
        {getEyes(mood)}

        {/* Mouth */}
        {getMouth(mood)}

        {/* Sport gear (on top of head) */}
        {getGear(sport)}

        {/* Cheek blush (happy moods) */}
        {(mood === 'correct' || mood === 'celebration' || mood === 'streak') && (
          <>
            <ellipse cx="28" cy="48" rx="4" ry="2.5" fill="#FF8A80" opacity="0.35" />
            <ellipse cx="64" cy="48" rx="4" ry="2.5" fill="#FF8A80" opacity="0.35" />
          </>
        )}

        {/* Arms */}
        <ellipse cx="22" cy="66" rx="6" ry="4" fill="#FFD180" transform="rotate(-15 22 66)" />
        <ellipse cx="70" cy="66" rx="6" ry="4" fill="#FFD180" transform="rotate(15 70 66)" />

        {/* Celebration: arms up */}
        {mood === 'celebration' && (
          <>
            <motion.ellipse cx="18" cy="54" rx="6" ry="4" fill="#FFD180" transform="rotate(-40 18 54)" animate={{ rotate: [-40, -50, -40] }} transition={{ duration: 0.5, repeat: 1 }} />
            <motion.ellipse cx="74" cy="54" rx="6" ry="4" fill="#FFD180" transform="rotate(40 74 54)" animate={{ rotate: [40, 50, 40] }} transition={{ duration: 0.5, repeat: 1 }} />
          </>
        )}

        {/* Mood effects */}
        {getMoodEffects(mood)}
      </svg>
    </motion.div>
  );
}
